import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * BUG-008 #13 — Spotlight content client.
 *
 * Thin HTTP wrapper for the public render endpoint:
 *   GET /v1/content/spotlight?lang=<de|en|fr|it> → SpotlightDTO (PUBLIC, no auth)
 *
 * `html` is already server-sanitized (gomarkdown → bluemonday allowlist,
 * see services/org/internal/spotlight). Consumers MUST render it via
 * [innerHTML] and MUST NOT call DomSanitizer.bypassSecurityTrust* on it —
 * Angular's built-in sanitizer is the documented second layer (SDR-014 §4).
 *
 * `title` is admin-controlled and NOT sanitized by the backend — consumers
 * MUST render it only via text interpolation ({{ }}), never via [innerHTML].
 *
 * Consumed by: the header button (app.component.ts), the /why page
 * (why-fatip.component.ts), and the platform-admin live preview
 * (spotlight-editor.component.ts).
 */

/** GET /v1/content/spotlight response (contentSpotlight handler in spotlight.go). */
export interface SpotlightDTO {
  /** Mirrors tenant_branding.spotlight_enabled (default true). */
  enabled: boolean;
  /** Resolved spotlight.title for the requested locale. Admin-controlled, unsanitized — interpolate only. */
  title: string;
  /** Sanitized HTML rendered from spotlight.body_md. Empty when no body_md configured. Render via [innerHTML] only. */
  html: string;
}

@Injectable({ providedIn: 'root' })
export class SpotlightService {
  private http = inject(HttpClient);
  private contentBase = `${environment.apiBaseUrl}/v1/content`;

  /**
   * PUBLIC — no auth required. Resolves the Spotlight title + sanitized
   * body HTML for the given locale (default 'de', unknown locale → 'de').
   */
  getSpotlight(lang: 'de' | 'en' | 'fr' | 'it'): Observable<SpotlightDTO> {
    return this.http.get<SpotlightDTO>(`${this.contentBase}/spotlight`, { params: { lang } });
  }
}
